import React, { useState, useRef, useEffect } from 'react';
import { Title } from 'components/Texts';
import { Line, colors } from 'globalStyle';
import { Hollow, hollowColorType } from 'components/Buttons';
import ArrowIcon from 'assets/icons/arrow_dropdown_primary.svg';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Grid, styled,
} from '@material-ui/core';

import ProjectTeam from './ProjectTeam';
import ProjectRisks from './ProjectRisks';
import ProjectDocuments from './ProjectDocuments';
import ProjectLongDescription from './ProjectLongDescription';

const SECTIONS = Object.freeze([
  {
    id: 'description',
    name: 'Descripción',
    componentWidth: '91.31%',
    Component: ProjectLongDescription,
  },
  {
    id: 'team',
    name: 'Equipo',
    componentWidth: '91.31%',
    Component: ProjectTeam,
    paddingMobile: '8px 16px 16px 3px',
  },
  {
    id: 'risks',
    name: 'Riesgos',
    componentWidth: '91.31%',
    Component: ProjectRisks,
  },
  {
    id: 'documents',
    name: 'Documentos',
    componentWidth: '91.31%',
    Component: ProjectDocuments,
  },
]);

export default function ProjectDetails(
  {
    isDesktop, activeItem, clearActiveItem,
    initialActiveItems = { description: SECTIONS[0] }, ...props
  },
) {
  const activeRef = useRef();
  const [activeItems, setActiveItems] = useState(isDesktop ? initialActiveItems : {});
  useEffect(() => {
    const item = SECTIONS.find((v) => v.id === activeItem);
    if (item) {
      setActiveItems({ [activeItem]: item });
      if (clearActiveItem) {
        clearActiveItem();
      }
      if (!isDesktop) {
        setTimeout(() => {
          activeRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 1000);
      }
    }
  }, [activeItem]);
  if (isDesktop) {
    const { Component, componentWidth } = Object.values(activeItems)[0];
    return (
      <Grid container alignItems="center" direction="column">
        <Grid container wrap="nowrap" spacing={2} style={{ width: '95.08%', marginBottom: '24px' }}>
          {SECTIONS.map((section) => (
            <Grid item container key={section.id}>
              <StyledHollow
                isSubmit
                fullWidth
                onClick={() => setActiveItems({ [section.id]: section })}
                active={activeItems[section.id]}
                onColorType={
                  activeItems[section.id] ? hollowColorType.primary : hollowColorType.GRAY
                }
              >
                {section.name}
              </StyledHollow>
            </Grid>
          ))}
        </Grid>
        <Line width="100%" color={colors.GRAY_10} />
        <Grid container style={{ marginTop: '50px', width: componentWidth }}>
          <Component isDesktop={isDesktop} {...props} />
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid container direction="column">
      {SECTIONS.map((section) => (
        <>
          <div
            style={{ visibility: 'hidden' }}
            ref={activeItems[section.id] && activeRef}
          />
          <StyledAccordion
            key={section.id}
            expanded={!!activeItems[section.id]}
            onChange={(_, isExpanded) => {
              setActiveItems(isExpanded ? { [section.id]: section } : {});
              setTimeout(() => {
                activeRef.current?.scrollIntoView({ behavior: 'smooth' });
              }, 500);
            }}
          >
            <StyledAccordionSummary expandIcon={(
              <Box
                component="img"
                src={ArrowIcon}
                alt="expandir u ocultar información"
              />
              )}
            >
              <Title color={colors.primary}>{section.name}</Title>
            </StyledAccordionSummary>
            <StyledAccordionDetails {...section}>
              <section.Component {...props} />
            </StyledAccordionDetails>
          </StyledAccordion>
        </>
      ))}
    </Grid>
  );
}

const StyledAccordion = styled(Accordion)({
  maxWidth: '100%',
  boxShadow: 'none',
  marginBottom: '20px',
  backgroundColor: colors.background.GRAY_3,
  '&::before': {
    backgroundColor: 'transparent',
  },
});

const StyledAccordionSummary = styled(AccordionSummary)({
  minHeight: '45px !important',
  maxHeight: '45px !important',
  backgroundColor: colors.WHITE,
  borderRadius: '10px !important',
  boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.08)',
});

const StyledAccordionDetails = styled(AccordionDetails)((props) => ({
  width: '100%',
  padding: !props.isDesktop && (props.paddingMobile || '8px 0px'),
}));

const StyledHollow = styled(Hollow)((props) => ({
  background: props.active && `${colors.primary} !important`,
  '&:hover': {
    color: `${colors.WHITE} !important`,
    border: `${colors.primary} !important`,
    background: `${props.active ? colors.primary : colors.light.primary} !important`,
  },
}));
